import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../Components/ui/button';
import { Input } from '../../Components/ui/input';
import { Badge } from '../../Components/ui/badge';
import { AlertTriangle } from 'lucide-react';
import {
  CalendarIcon,
  FileIcon,
  CaretSortIcon,
  ArrowRightIcon,
  DotsHorizontalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@radix-ui/react-icons';
import { format } from 'date-fns';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../Components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../Components/ui/table';
import { Calendar } from '../../Components/ui/calendar';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { DialogFooter } from '../../Components/ui/dialog';
import { DialogHeader } from '../../Components/ui/dialog';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import { RootState } from '@/src/State/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../Components/ui/dropdown-menu';
import { DateRange } from 'react-day-picker';
import { convertUTCToLocal } from '../../../src/utils/convertUTCToLocal';
import { useToast } from '../../Components/ui/use-toast';
import AdminCampaignListDropdown from '../../Components/Filter/AdminCampaignListDropdown';
import CampaignSelectionSection from '../AdminPages/QuickSend/QuickSendCard';
import QuickSendCard from '../AdminPages/QuickSend/QuickSendCard';

interface Campaign {
  campaign_id: number;
  campaign_name: string;
  workspace_name: string;
  advertiser_workspace_id: number;
  status: string;
  channels: string[];
  start_date: string;
  end_date: string;
  sent: number;
  delivered: number;
  delivered_whatsapp_count: number;
  delivered_sms_count: number;
  total_messages_sent: number;
  traveller_count_total: number;
  admin_approval: string;
  status_preview: string;
  sent_quick_send_count: number;
  delivered_quick_send_count: number;
  sent_test_mode_count: number;
  delivered_test_mode_count: number;
}

// Channel Carousel Component (with automatic right-to-left animation)
const ChannelCarousel: React.FC<{ channels: string[] }> = ({ channels }) => {
  const filteredChannels = channels.filter(Boolean);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(true);
  const badgeRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [offsets, setOffsets] = useState<number[]>([]);

  // Measure badge widths for accurate sliding
  useEffect(() => {
    if (filteredChannels.length > 1) {
      setTimeout(() => {
        const allOffsets: number[] = [];
        let sum = 0;
        for (let i = 0; i < filteredChannels.length; i++) {
          allOffsets.push(sum);
          const w = badgeRefs.current[i]?.offsetWidth ?? 0;
          sum += w + 2; // 2px gap
        }
        setOffsets(allOffsets);
      }, 0);
    }
  }, [filteredChannels.length, channels.join(',')]);

  // Animation timing
  useEffect(() => {
    if (filteredChannels.length <= 1) return;
    let timeout: ReturnType<typeof setTimeout>;
    if (isSliding) {
      timeout = setTimeout(() => setIsSliding(false), 4000); // 4s animation
    } else {
      timeout = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % filteredChannels.length);
        setIsSliding(true);
      }, 1000); // 1s pause
    }
    return () => clearTimeout(timeout);
  }, [isSliding, filteredChannels.length]);

  if (filteredChannels.length === 0) {
    return <span className='text-gray-400'>-</span>;
  }
  if (filteredChannels.length === 1) {
    // Single badge with color logic
    const channel = filteredChannels[0];
    return (
      <div className='flex items-center h-7'>
        <Badge
          className={
            channel === 'WhatsApp'
              ? ''
              : channel === 'SMS'
                ? ''
                : channel === 'Push Notification'
                  ? ''
                  : channel === 'Email'
                    ? 'bg-blue-400'
                    : 'bg-gray-400'
          }
          style={
            channel === 'WhatsApp'
              ? { backgroundColor: '#479E98', color: '#fff' }
              : channel === 'SMS'
                ? { backgroundColor: '#DFA548', color: '#fff' }
                : channel === 'Push Notification' || channel === 'Push'
                  ? { backgroundColor: '#B87867', color: '#fff' }
                  : channel === 'RCS' || channel === 'RCS messages'
                    ? { backgroundColor: '#61177E', color: '#fff' }
                    : {}
          }
        >
          {channel}
        </Badge>
      </div>
    );
  }

  // Multiple badges
  return (
    <div className='relative h-7 w-[120px] overflow-hidden'>
      <div
        className='flex items-center'
        style={{
          gap: '2px',
          transform: `translateX(-${offsets[currentIndex] ?? 0}px)`,
          transition: isSliding
            ? 'transform 4s cubic-bezier(0.5,0,0.5,1)'
            : 'none',
        }}
      >
        {filteredChannels.map((channel, idx) => (
          <div
            key={idx}
            ref={(el) => (badgeRefs.current[idx] = el)}
            className='flex items-center'
            style={{ margin: 0, padding: 0 }}
          >
            <Badge
              className={
                channel === 'WhatsApp'
                  ? ''
                  : channel === 'SMS'
                    ? ''
                    : channel === 'Push Notification'
                      ? ''
                      : channel === 'Email'
                        ? 'bg-blue-400'
                        : 'bg-gray-400'
              }
              style={
                channel === 'WhatsApp'
                  ? { backgroundColor: '#479E98', color: '#fff' }
                  : channel === 'SMS'
                    ? { backgroundColor: '#DFA548', color: '#fff' }
                    : channel === 'Push Notification' || channel === 'Push'
                      ? { backgroundColor: '#B87867', color: '#fff' }
                      : channel === 'RCS' || channel === 'RCS messages'
                        ? { backgroundColor: '#61177E', color: '#fff' }
                        : {}
              }
            >
              {channel}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusPreviewBadge: React.FC<{
  value?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ value, onClick }) => {
  const raw = (value ?? '').trim();
  const hasWarning = /with warning symbol/i.test(raw);
  const base = raw.replace(/with warning symbol/gi, '').trim();

  const norm = base.toLowerCase().replace(/\s+/g, ' ');
  type Key = 'live' | 'partially live' | 'in review' | 'rejected' | 'pending';
  const key: Key =
    norm === 'live'
      ? 'live'
      : norm === 'partially live' || norm === 'partialy live'
        ? 'partially live'
        : norm === 'in review' || norm === 'inreview'
          ? 'in review'
          : norm === 'rejected'
            ? 'rejected'
            : 'pending';

  let colorClasses = '';
  switch (key) {
    case 'live':
      colorClasses =
        '!bg-green-700 text-white hover:!bg-green-700 hover:text-white';
      break;
    case 'partially live':
      colorClasses =
        '!bg-green-300 text-gray-900 hover:!bg-green-300 hover:text-gray-900';
      break;
    case 'in review':
      colorClasses =
        '!bg-yellow-400 text-gray-900 hover:!bg-yellow-400 hover:text-gray-900';
      break;
    case 'rejected':
      colorClasses =
        '!bg-red-600 text-white hover:!bg-red-600 hover:text-white';
      break;
    case 'pending':
    default:
      colorClasses =
        '!bg-gray-400 text-white hover:!bg-gray-400 hover:text-white';
      break;
  }

  const label = key.replace(/\b\w/g, (c) => c.toUpperCase());

  // ✨ hover / focus enlargement (same look as your other badge)
  const hoverFx =
    'transform-gpu transition-transform duration-200 ease-out ' +
    'group-hover:scale-[1.08] group-focus-visible:scale-[1.05] active:scale-100 ' +
    'group-hover:shadow-lg';

  return (
    <button
      type='button'
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick?.(e as any);
      }}
      className='group inline-flex items-center p-0 m-0 bg-transparent hover:bg-transparent focus:outline-none focus:ring-0'
      aria-label={`Open campaign status (${label}${hasWarning ? ' – warning' : ''})`}
    >
      <div className='inline-flex items-center gap-1'>
        {/* pointer-events-none keeps color hover fixed; group-hover handles the scale */}
        <Badge
          className={`pointer-events-none inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium ${colorClasses} ${hoverFx}`}
        >
          <span>{label}</span>
        </Badge>

        {hasWarning && (
          <AlertTriangle
            size={23}
            className='ml-1 text-red-600 transform-gpu transition-transform duration-200 ease-out group-hover:scale-[1.08]'
            aria-hidden='true'
          />
        )}
      </div>
    </button>
  );
};

const DatePickerWithRange: React.FC<{
  className?: string;
  fromDate?: Date;
  toDate?: Date;
  setCampaignList: React.Dispatch<React.SetStateAction<Campaign[]>>;
  getCampaignList: (params: {
    page: number;
    rowsPerPage: number;
    searchTerm: string;
    startDate?: string;
    endDate?: string;
  }) => void;
  setInitialCampaign: React.Dispatch<React.SetStateAction<boolean>>;
  initialcampaign: boolean;
  targetCountryList: any[];
  roamingCountryList: any[];
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  setIsClearingDate: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  className,
  setInitialCampaign,
  initialcampaign,
  dateRange,
  setDateRange,
  setIsClearingDate,
}) => {
  return (
    <div className={className}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant='outline'
            className='min-w-[150px] w-auto justify-start text-left font-normal mt-0'
          >
            <CalendarIcon className='mr-2 h-4 w-4' />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, 'LLL dd, y')} -{' '}
                  {format(dateRange.to, 'LLL dd, y')}
                </>
              ) : (
                format(dateRange.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-auto p-0' align='start'>
          <Calendar
            initialFocus
            mode='range'
            defaultMonth={dateRange?.from ?? new Date()}
            selected={dateRange}
            onSelect={(range: DateRange | undefined) => {
              if (
                !range?.from &&
                !range?.to &&
                (dateRange?.from || dateRange?.to)
              ) {
                setIsClearingDate(true);
              }
              setDateRange(range);
              if ((range?.from && range?.to) || (!range?.from && !range?.to)) {
                setInitialCampaign(false);
              }
            }}
            numberOfMonths={1}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

const AdminCampaignList: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const formatDate = (date: Date | undefined): string | undefined => {
    return date ? date.toISOString().split('T')[0] : undefined;
  };
  const toast = useToast();
  const [openMenuRowId, setOpenMenuRowId] = useState<number | null>(null);
  const [campaignList, setCampaignList] = useState<Campaign[]>([]);
  const navigate = useNavigate();
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<number | null>(null);
  const [isSorted, setIsSorted] = useState(false);
  const [originalCampaigns, setOriginalCampaigns] = useState<Campaign[]>([]);
  const [checkboxSelectedRows, setCheckboxSelectedRows] = useState<number[]>(
    [],
  );
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isApiExecuting, setIsApiExecuting] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isClearingSearch, setIsClearingSearch] = useState(false);
  const [isClearingDate, setIsClearingDate] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [hasCampaigns, setHasCampaigns] = useState(false);
  const [initialcampaign, setInitialCampaign] = useState(true);
  const dispatch = useDispatch();
  const [isExporting, setIsExporting] = useState(false);

  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [detailedSummaryModal, setDetailedSummaryModal] = useState<{
    isOpen: boolean;
    campaign: Campaign | null;
  }>({ isOpen: false, campaign: null });
  //   const [quickSendModal, setQuickSendModal] = useState({
  //   isOpen: false,
  //   campaign: null
  // });
  const [quickSendModal, setQuickSendModal] = useState<{
    isOpen: boolean;
    campaign: Campaign | null;
  }>({ isOpen: false, campaign: null });

  const [deliveryResult, setDeliveryResult] = useState<{
    isOpen: boolean;
    data: any | null;
  }>({ isOpen: false, data: null });

  const [sendTrigger, setSendTrigger] = useState(0);

  const apiUrlAdminAcc = useSelector(
    (state: RootState) => state.authentication.adminUrl,
  );
  const apiUrlAdvAcc = useSelector(
    (state: RootState) => state.authentication.apiURL,
  );

  const QuickTestUrl = useSelector(
    (state: RootState) => state.authentication.QuickTestUrl,
  );

  const workspaceTimezone = useSelector(
    (state: RootState) => state.authentication.workspaceTimezone,
  );

  const admin_workspace_id = useSelector(
    (state: RootState) => state.authentication.workspace_id,
  );

  const CurrentAID = useSelector(
    (state: RootState) => state.authentication.account_id,
  );
  // In your Admin component state
  const [filterData, setFilterData] = useState<{
    filter: string;
    value: string;
  }>({
    filter: 'None',
    value: '',
  });

  // Add debounced filter value for Accounts search
  const [debouncedFilterValue, setDebouncedFilterValue] = useState('');

  const currentCampaigns = campaignList;

  useEffect(() => {
    setTimeout(() => {
      document.body.style.pointerEvents = '';
    }, 500);
  }, [quickSendModal.isOpen]);

  const gotoCampaignStatus = (id: number, channels: string[]) =>
    navigate('/adminNavbar/campaignstatus', {
      state: { campaignId: id, channels },
    });

  const handleCheckboxRowSelect = (id: number) => {
    setCheckboxSelectedRows((prev) => {
      const newSelectedRows = prev.includes(id)
        ? prev.filter((rowId) => rowId !== id)
        : [...prev, id];
      setIsAllSelected(newSelectedRows.length === currentCampaigns.length);
      return newSelectedRows;
    });
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setCheckboxSelectedRows([]);
    } else {
      const allIds = currentCampaigns.map((campaign) => campaign.campaign_id);
      setCheckboxSelectedRows(allIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  // const getCampaignListWithArgs = async (params: {
  //   page: number;
  //   rowsPerPage: number;
  //   searchTerm: string;
  //   startDate?: string;
  //   endDate?: string;
  // }) => {
  //   if (!apiUrlAdminAcc) return;
  //   setIsLoading(true);
  //   try {
  //     const searchParam = params.searchTerm?.trim() || null;
  //     const response = await axios.get(`${apiUrlAdminAcc}/GetCampaignListAdminWithPaginationWithStatusPreview`, {
  //       params: {
  //         page: params.page,
  //         rowsPerPage: params.rowsPerPage,
  //         searchTerm: searchParam,
  //         startDate: params.startDate,
  //         endDate: params.endDate,
  //       },
  //     });
  //     if (response.data.status === "Success") {
  //       setCampaignList(response.data.campaignList);
  //       setTotalCount(response.data.campaignCount);
  //     } else {
  //       setCampaignList([]);
  //       setTotalCount(0);
  //     }
  //   } catch (error) {
  //     setCampaignList([]);
  //     setTotalCount(0);
  //   } finally {
  //     setIsLoading(false);
  //     setIsClearingSearch(false);
  //     setIsClearingDate(false);
  //   }
  // };

  // Debounce effect for Accounts filter (500ms delay)
  useEffect(() => {
    // Only debounce when filter is "Accounts"
    if (filterData.filter === 'Accounts') {
      const handler = setTimeout(() => {
        setDebouncedFilterValue(filterData.value);
      }, 500);

      return () => {
        clearTimeout(handler);
      };
    } else {
      // For other filters, use value immediately
      setDebouncedFilterValue(filterData.value);
    }
  }, [filterData]);

  // Update getCampaignListWithArgs to accept filter parameters
  const getCampaignListWithArgs = async (params: {
    page: number;
    rowsPerPage: number;
    searchTerm: string;
    startDate?: string;
    endDate?: string;
    filter?: string;
    subFilter?: string;
  }) => {
    if (!apiUrlAdminAcc) return;
    setIsLoading(true);
    try {
      const searchParam = params.searchTerm?.trim() || null;
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetCampaignListAdminWithPaginationWithStatusPreview`,
        {
          params: {
            page: params.page,
            rowsPerPage: params.rowsPerPage,
            searchTerm: searchParam,
            startDate: params.startDate,
            endDate: params.endDate,
            ...(params.filter &&
              params.filter !== 'None' && {
                filter: params.filter,
                subFilter: params.subFilter,
              }),
          },
        },
      );
      if (response.data.status === 'Success') {
        setCampaignList(response.data.campaignList);
        setTotalCount(response.data.campaignCount);
      } else {
        setCampaignList([]);
        setTotalCount(0);
      }
    } catch (error) {
      setCampaignList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
      setIsClearingSearch(false);
      setIsClearingDate(false);
      if (isFirstLoading) setIsFirstLoading(false);
    }
  };
  // const getCampaignList = async () => {
  //   await getCampaignListWithArgs({
  //     page: currentPage,
  //     rowsPerPage,
  //     searchTerm: " ",
  //     startDate: undefined,
  //     endDate: undefined,
  //   });
  // };

  const handleQuickSend = async (payload: any) => {
    try {
      await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
        UserAccountId: CurrentAID,
        CampaignId: payload.campaign_id,
        AdvertiserWorkspaceId: admin_workspace_id,
        Action: 'Quick Send Message',
        ActionPage: 'Quick Send',
        Status: 'Success',
        BeforeData: null,
        AfterData: JSON.stringify(payload),
        Comments: 'Quick send initiated',
      });

      const response = await axios.post(
        `${QuickTestUrl}/api/send-message`,
        payload,
      );
      console.log('Send response:', response.data);
      setQuickSendModal({ isOpen: false, campaign: null });

      // ✅ Open delivery result dialog on success
      if (response.data?.status === 'success') {
        setDeliveryResult({ isOpen: true, data: response.data });
        await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
          UserAccountId: CurrentAID,
          CampaignId: payload.campaign_id,
          AdvertiserWorkspaceId: admin_workspace_id,
          Action: 'Quick Send Message',
          ActionPage: 'Quick Send',
          Status: 'Success',
          BeforeData: JSON.stringify(payload),
          AfterData: JSON.stringify(response.data),
          Comments: 'Quick send message sent successfully',
        });
      }
    } catch (error) {
      await axios.post(`${apiUrlAdvAcc}/InsertOverAllAuditHistory`, {
        UserAccountId: CurrentAID,
        CampaignId: payload.campaign_id,
        AdvertiserWorkspaceId: admin_workspace_id,
        Action: 'Quick Send Message',
        ActionPage: 'Quick Send',
        Status: 'Failed',
        BeforeData: JSON.stringify(payload),
        AfterData: null,
        Comments: 'Quick send message failed due to an exception',
      });
      console.error('Send failed:', error);
    }
  };

  // const handleQuickSend = async (payload: any) => {
  //   try {
  //     // ✅ HARDCODED RESPONSE FOR TESTING — replace with real API call later
  //     const mockResponse = {
  //       data: {
  //         status: "success",
  //         total: 2,
  //         success_count: 2,
  //         failure_count: 0,
  //         results: [
  //           {
  //             msisdn: "917094472849",
  //             status: "success",
  //             used_msg_count: 1,
  //             remarks: "No remarks"
  //           },
  //           {
  //             msisdn: "917806920892",
  //             status: "success",
  //             used_msg_count: 1,
  //             remarks: "No remarks"
  //           }
  //         ]
  //       }
  //     };

  //     // const response = await axios.post(`${apiUrlAdminAcc}/QuickSend`, payload);
  //     // console.log("Send response:", response.data);

  //     setQuickSendModal({ isOpen: false, campaign: null });

  //     if (mockResponse.data?.status === "success") {
  //       setDeliveryResult({ isOpen: true, data: mockResponse.data });
  //     }

  //   } catch (error) {
  //     console.error("Send failed:", error);
  //   }
  // };

  // Update getCampaignList
  const getCampaignList = async () => {
    await getCampaignListWithArgs({
      page: currentPage,
      rowsPerPage,
      searchTerm: ' ',
      startDate: undefined,
      endDate: undefined,
      filter: filterData.filter,
      subFilter: debouncedFilterValue, // Use debounced value
    });
  };

  const totalPages: number = Math.ceil(totalCount / rowsPerPage);
  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleDeleteClick = (campaignId: number) => {
    setCampaignToDelete(campaignId);
    setIsAlertOpen(true);
  };

  const handleClose = () => {
    setIsAlertOpen(false);
  };

  const confirmDelete = async () => {
    setIsApiExecuting(true);
    try {
      const response = await axios.delete(
        `${apiUrlAdminAcc}/DeleteCampaignById?CampaignId=${campaignToDelete}`,
      );

      if (response.data.status === 'Success') {
        setIsAlertOpen(false);

        const duration = 1000;
        // show toast
        toast.toast({
          title: 'Success',
          description: 'The campaign was deleted successfully',
          duration, // ✅ shadcn-style toast prop
        });

        // refresh after the toast auto-hides
        setTimeout(() => {
          getCampaignList();
        }, duration);
      } else {
        toast.toast({
          title: 'Failure',
          description: 'Error while deleting the campaign',
        });
      }
    } catch {
      toast.toast({
        title: 'Failure',
        description: 'Error while deleting the campaign',
      });
    } finally {
      setIsApiExecuting(false);
    }
  };

  const handleExportButtonClick = async () => {
    if (!apiUrlAdminAcc) return;

    setIsExporting(true);
    try {
      let startDate: string | undefined;
      let endDate: string | undefined;
      const bothDatesSelected = dateRange?.from && dateRange?.to;
      if (bothDatesSelected) {
        startDate = format(dateRange.from!, 'yyyy-MM-dd');
        endDate = format(dateRange.to!, 'yyyy-MM-dd');
      }

      const searchParam = searchTerm?.trim() || null;

      const response = await axios.get(
        `${apiUrlAdminAcc}/GetCampaignListAdminWithOutPaginationWithStatusPreview`,
        {
          params: {
            searchTerm: searchParam,
            startDate,
            endDate,
            ...(filterData.filter &&
              filterData.filter !== 'None' && {
                filter: filterData.filter,
                subFilter: debouncedFilterValue,
              }),
          },
        },
      );

      if (response.data.status !== 'Success') {
        return;
      }

      const fullCampaignList: Campaign[] = response.data.campaignList || [];

      const filteredData = fullCampaignList.map(
        ({
          campaign_id,
          campaign_name,
          workspace_name,
          channels,
          status,
          admin_approval,
          start_date,
          end_date,
          traveller_count_total,
          sent,
          total_messages_sent,
          delivered,
          delivered_whatsapp_count,
          delivered_sms_count,
          sent_quick_send_count,
          delivered_quick_send_count,
          sent_test_mode_count,
          delivered_test_mode_count,
        }) => ({
          campaign_idD: campaign_id,
          campaign_name: campaign_name,
          workspace_Name: workspace_name,
          channel: (channels || []).filter(Boolean).join(', '),
          campaign_status: status,
          approval_status: admin_approval,
          start_date: start_date,
          end_date: end_date,
          total_travellers: traveller_count_total,
          total_targeted_traveller_count: sent,
          total_messages_sent: total_messages_sent,
          total_delivered_count: delivered,
          delivered_whatsapp_count: delivered_whatsapp_count,
          delivered_sms_count: delivered_sms_count,
          quick_send_sent_messages: sent_quick_send_count,
          quick_send_delivered_messages: delivered_quick_send_count,
          test_mode_sent_messages: sent_test_mode_count,
          test_mode_delivered_messages: delivered_test_mode_count,
        }),
      );

      const worksheet = XLSX.utils.json_to_sheet(filteredData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Admin_Campaign_List');
      XLSX.writeFile(workbook, 'AdminCampaignList.xlsx');
    } catch (error) {
      console.error('Error exporting campaign list:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const sortCampaignList = (tableHeader: string) => {
    let field: keyof Campaign;
    let type: 'string' | 'number' | 'date' = 'string';

    switch (tableHeader) {
      case 'ByCampaignName':
        field = 'campaign_name';
        type = 'string';
        break;
      case 'ByCampaignAccount':
        field = 'workspace_name';
        type = 'string';
        break;
      case 'ByCampaignStatus':
        field = 'status';
        type = 'string';
        break;
      case 'ByCampaignApprovalStatus': // <-- ADD THIS
        field = 'admin_approval';
        type = 'string';
        break;
      case 'ByCampaignDate':
        field = 'start_date';
        type = 'date';
        break;
      case 'ByCampaignSent':
        field = 'sent';
        type = 'number';
        break;
      case 'ByTotalTravellers':
        field = 'traveller_count_total';
        type = 'number';
        break;
      case 'ByMessagesSent':
        field = 'total_messages_sent';
        type = 'number';
        break;
      default:
        return;
    }

    // Toggle direction if the same field, otherwise default to ascending
    let direction: 'asc' | 'desc' = 'asc';
    if (sortField === field) {
      direction = sortDirection === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortDirection(direction);

    const sortedCampaigns = [...campaignList].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      if (type === 'number') {
        aValue = Number(aValue);
        bValue = Number(bValue);
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else if (type === 'date') {
        aValue = Date.parse(aValue as string);
        bValue = Date.parse(bValue as string);
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        return direction === 'asc'
          ? String(aValue || '').localeCompare(String(bValue || ''))
          : String(bValue || '').localeCompare(String(aValue || ''));
      }
    });
    setCampaignList(sortedCampaigns);
  };

  const toggleCampaignStatus = async (
    campaignId: number,
    action: 'paused' | 'active',
  ) => {
    if (!campaignId || !action) return;

    let isSuccess = false; // track success/failure for audit

    try {
      const res = await axios.put(`${apiUrlAdvAcc}/ToggleCampaignPauseResume`, {
        campaignId,
        action,
      });

      if (res.data?.status === 'Success') {
        isSuccess = true;
        await getCampaignList(); // refresh table
        toast.toast({
          title: 'Success',
          description: 'The campaign status was updated successfully.',
        });
      } else {
        toast.toast({
          title: 'Failure',
          description: 'Failed to update the campaign status.',
        });
      }
    } catch (error: any) {
      console.error('❌ Error toggling campaign:', error);
      toast.toast({
        title: 'Failure',
        description:
          'Something went wrong while trying to update the campaign status.',
      });
    } finally {
      try {
        // ✅ Construct audit payload
        const auditAfterData = {
          campaignId,
          newStatus: action,
          timestamp: new Date().toISOString(),
        };

        const auditPayload = {
          UserAccountId: CurrentAID, // replace with your actual user ID variable
          CampaignId: campaignId,
          AdvertiserWorkspaceId: admin_workspace_id, // replace with workspace variable
          Action:
            action === 'paused'
              ? 'Campaign Paused by Admin'
              : 'Campaign Resumed by Admin',
          ActionPage: 'Admin Campaign Management',
          Status: isSuccess ? 'Success' : 'Failed',
          BeforeData: null, // fetched automatically on backend
          AfterData: JSON.stringify(auditAfterData),
          IpAddress: null, // backend fills automatically
          Comments: isSuccess
            ? `Campaign ${action} successfully.`
            : `Failed to ${action} the campaign.`,
        };

        console.log('📝 Sending Audit Payload:', auditPayload);

        // ✅ Send audit log
        const auditResponse = await axios.post(
          `${apiUrlAdvAcc}/InsertOverAllAuditHistory`,
          auditPayload,
        );

        if (auditResponse.data?.Status === 'Success') {
          console.log(`✅ Audit inserted successfully for ${action} action.`);
        } else {
          console.warn(
            '⚠️ Audit insertion did not return success:',
            auditResponse.data,
          );
        }
      } catch (auditError: any) {
        console.error('❌ Failed to insert audit:', auditError);
        toast.toast({
          title: 'Audit Error',
          description: 'Audit logging failed after campaign status update.',
        });
      }
    }
  };

  useEffect(() => {
    if (!apiUrlAdminAcc) return;
    getCampaignList();
    // eslint-disable-next-line
  }, [apiUrlAdminAcc]);

  useEffect(() => {
    setHasCampaigns(campaignList.length > 0);
  }, [campaignList]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // useEffect(() => {
  //   if (searchTerm !== debouncedSearchTerm) return;
  //   let startDate: string | undefined;
  //   let endDate: string | undefined;
  //   const bothDatesSelected = dateRange?.from && dateRange?.to;
  //   const bothDatesCleared = !dateRange?.from && !dateRange?.to;
  //   const singleDateSelected = dateRange?.from && !dateRange?.to;
  //   if (bothDatesSelected) {
  //     startDate = format(dateRange.from!, "yyyy-MM-dd");
  //     endDate = format(dateRange.to!, "yyyy-MM-dd");
  //   }
  //   if ((bothDatesSelected || bothDatesCleared || initialcampaign) && !singleDateSelected) {
  //     getCampaignListWithArgs({
  //       page: currentPage,
  //       rowsPerPage,
  //       searchTerm: debouncedSearchTerm,
  //       startDate,
  //       endDate
  //     });
  //   }
  //   // eslint-disable-next-line
  // }, [currentPage, rowsPerPage, debouncedSearchTerm, dateRange?.from, dateRange?.to, initialcampaign]);
  // Update the useEffect to include filterData

  // Update the main useEffect - use debouncedFilterValue instead of filterData.value
  useEffect(() => {
    if (searchTerm !== debouncedSearchTerm) return;
    let startDate: string | undefined;
    let endDate: string | undefined;
    const bothDatesSelected = dateRange?.from && dateRange?.to;
    const bothDatesCleared = !dateRange?.from && !dateRange?.to;
    const singleDateSelected = dateRange?.from && !dateRange?.to;
    if (bothDatesSelected) {
      startDate = format(dateRange.from!, 'yyyy-MM-dd');
      endDate = format(dateRange.to!, 'yyyy-MM-dd');
    }
    if (
      (bothDatesSelected || bothDatesCleared || initialcampaign) &&
      !singleDateSelected
    ) {
      getCampaignListWithArgs({
        page: currentPage,
        rowsPerPage,
        searchTerm: debouncedSearchTerm,
        startDate,
        endDate,
        filter: filterData.filter,
        subFilter: debouncedFilterValue, // Use debounced value here
      });
    }
    // eslint-disable-next-line
  }, [
    currentPage,
    rowsPerPage,
    debouncedSearchTerm,
    dateRange?.from,
    dateRange?.to,
    initialcampaign,
    filterData.filter,
    debouncedFilterValue,
  ]);

  const handleNavigate =
    (
      campaignId: number,
      channelType: string,
      status: string,
      campaignWorkspaceId: number,
    ) =>
    (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      navigate('/adminNavbar/admincampaignapproval', {
        state: { campaignId, channelType, status, campaignWorkspaceId },
      });
    };

  return (
    <div>
      <ToastContainer />
      {isFirstLoading && (
        <div className='flex flex-col items-center justify-center h-[500px]'>
          <CircularProgress color='primary' />
        </div>
      )}

      {hasCampaigns ||
      searchTerm.trim() !== '' ||
      isClearingSearch ||
      isClearingDate ||
      (dateRange?.from && dateRange?.to) ||
      (filterData.filter !== 'None' && filterData.filter !== '') ? (
        <>
          <div className='mt-4'>
            {/* --- Filter Row --- */}
            <div className='flex flex-wrap items-center justify-between gap-4 -mt-6'>
              <div className='flex flex-wrap justify-start w-full md:w-auto'>
                <Input
                  placeholder='Search campaign by name...'
                  className='w-full md:w-[350px] text-[14px] font-normal text-[#171717] !mt-0'
                  value={searchTerm}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (searchTerm.trim() !== '' && newValue.trim() === '') {
                      setIsClearingSearch(true);
                    }
                    setSearchTerm(newValue);
                    if (currentPage !== 1) setCurrentPage(1);
                  }}
                />
              </div>
              <div className='flex flex-wrap items-center justify-end gap-10 w-full md:w-auto'>
                <div className='flex-shrink-0 -mr-5'>
                  <AdminCampaignListDropdown
                    filterData={filterData}
                    setFilterData={setFilterData}
                  />
                </div>
                <div className='flex-shrink-0 -mr-5'>
                  <DatePickerWithRange
                    initialcampaign={initialcampaign}
                    setInitialCampaign={setInitialCampaign}
                    getCampaignList={getCampaignList}
                    setCampaignList={setCampaignList}
                    targetCountryList={[]}
                    roamingCountryList={[]}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    setIsClearingDate={setIsClearingDate}
                  />
                </div>

                <div className='flex-shrink-0'>
                  <Button
                    variant='outline'
                    className='w-24 text-[#020617] !mt-0'
                    onClick={handleExportButtonClick}
                    disabled={isExporting}
                  >
                    {isExporting ? (
                      'Exporting...'
                    ) : (
                      <>
                        <FileIcon
                          style={{ width: '14px', height: '14px' }}
                          className='mr-1 font-bold stroke-[2]'
                        />{' '}
                        Export
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
            {/* --- Table --- */}
            <div className='rounded-md border mt-8'>
              <div className='max-h-[60vh] overflow-y-auto'>
                <Table
                  className='rounded-xl whitespace-nowrap border-gray-200'
                  style={{ color: '#020202', fontSize: '15px' }}
                >
                  <TableHeader>
                    <TableRow className='sticky top-0 z-10 bg-white hover:bg-white'>
                      {/* Checkbox + Campaign Name Header */}
                      <TableHead className='pl-6 pr-3 py-3 font-medium'>
                        <div className='flex items-center gap-3'>
                          <input
                            type='checkbox'
                            className={`accent-gray-700`}
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                          />
                          <span>Campaign</span>
                          <CaretSortIcon
                            onClick={() => sortCampaignList('ByCampaignName')}
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>
                      <TableHead className='py-3 px-3 font-medium'>
                        <div className='flex items-center gap-2'>
                          Account
                          <CaretSortIcon
                            onClick={() =>
                              sortCampaignList('ByCampaignAccount')
                            }
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>
                      <TableHead className='py-3 px-3 font-medium w-[140px]'>
                        <div className='flex items-center gap-2'>Channel</div>
                      </TableHead>
                      <TableHead className='py-3 px-3 font-medium'>
                        <div className='flex items-center gap-2'>
                          Campaign Status
                          <CaretSortIcon
                            onClick={() => sortCampaignList('ByCampaignStatus')}
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>
                      {/* <TableHead className="py-3 px-3 font-medium">
                        <div className="flex items-center gap-2">
                          Admin Approval Status
                          <CaretSortIcon onClick={() => sortCampaignList("ByCampaignApprovalStatus")} className="cursor-pointer" />
                        </div>
                      </TableHead> */}
                      <TableHead className='py-3 px-3 font-medium'>
                        <div className='flex items-center gap-2'>
                          Setup Preview
                        </div>
                      </TableHead>
                      <TableHead className='py-3 px-3 font-medium'>
                        <div className='flex items-center gap-2'>
                          Schedule
                          <CaretSortIcon
                            onClick={() => sortCampaignList('ByCampaignDate')}
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>
                      <TableHead className='py-3 px-3 font-medium'>
                        <div className='flex items-center gap-2'>
                          Total Travellers
                          <CaretSortIcon
                            onClick={() =>
                              sortCampaignList('ByTotalTravellers')
                            }
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>

                      <TableHead className='py-3 px-3 font-medium'>
                        {/* <div className="flex items-center gap-2">
                        People Reached
                        <CaretSortIcon onClick={() => sortCampaignList("ByCampaignSent")} className="cursor-pointer" />
                      </div> */}
                        <div className='flex items-center gap-2'>
                          Targeted Travellers
                          <CaretSortIcon
                            onClick={() => sortCampaignList('ByCampaignSent')}
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>
                      <TableHead className='py-3 px-3 font-medium'>
                        <div className='flex items-center gap-2'>
                          Messages Sent
                          <CaretSortIcon
                            onClick={() => sortCampaignList('ByMessagesSent')}
                            className='cursor-pointer'
                          />
                        </div>
                      </TableHead>
                      <TableHead className='w-12 py-3'></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentCampaigns.length > 0 &&
                    !isLoading &&
                    !isClearingSearch ? (
                      currentCampaigns.map((campaign) => {
                        const isSelected = checkboxSelectedRows.includes(
                          campaign.campaign_id,
                        );
                        return (
                          <TableRow
                            key={campaign.campaign_id}
                            className={isSelected ? 'bg-gray-200' : ''}
                          >
                            {/* Checkbox + Campaign Name Cell */}
                            <TableCell className='pl-6 pr-3 py-4'>
                              <div className='flex items-center gap-3'>
                                <input
                                  type='checkbox'
                                  className='accent-gray-700'
                                  checked={isSelected}
                                  onChange={() =>
                                    handleCheckboxRowSelect(
                                      campaign.campaign_id,
                                    )
                                  }
                                />
                                <span
                                  className='hover:text-blue-500 text-black text-[14px] cursor-pointer font-normal'
                                  onClick={handleNavigate(
                                    campaign.campaign_id,
                                    (campaign.channels &&
                                      campaign.channels.filter(Boolean)[0]) ||
                                      '',
                                    campaign.status,
                                    campaign.advertiser_workspace_id,
                                  )}
                                >
                                  {campaign.campaign_name}
                                </span>
                              </div>
                            </TableCell>
                            {/* Account Cell */}
                            <TableCell className='py-4 px-3 align-middle text-left'>
                              <span className='text-[#020202] text-[15px]'>
                                {campaign.workspace_name}
                              </span>
                            </TableCell>
                            {/* Channel Cell with Carousel */}
                            <TableCell className='py-4 px-3 align-middle w-[140px]'>
                              <ChannelCarousel
                                channels={campaign.channels || []}
                              />
                            </TableCell>
                            {/* Status Cell */}
                            <TableCell className='py-4 px-3 align-middle text-left'>
                              {campaign.status}
                            </TableCell>
                            {/* <TableCell className="py-4 px-3 align-middle text-left">
                              {campaign.admin_approval}
                            </TableCell> */}
                            <TableCell className='py-4 px-3 align-middle text-left'>
                              <StatusPreviewBadge
                                value={campaign.status_preview}
                                onClick={(e) => {
                                  e.stopPropagation(); // avoid triggering any parent click
                                  gotoCampaignStatus(
                                    campaign.campaign_id,
                                    campaign.channels || [],
                                  );
                                }}
                              />
                            </TableCell>
                            {/* Schedule Cell */}
                            {/* <TableCell className="py-4 px-3 align-middle">
                              <div className="flex items-center gap-2">
                                {new Date(campaign.start_date).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                })}
                                , {new Date(campaign.start_date).getFullYear()}
                                <ArrowRightIcon />
                                {new Date(campaign.end_date).toLocaleDateString("en-GB", {
                                  day: "numeric",
                                  month: "short",
                                })}
                                , {new Date(campaign.end_date).getFullYear()}
                              </div>
                            </TableCell> */}

                            <TableCell className='py-4 px-3 align-middle'>
                              <div className='flex items-center gap-2'>
                                {
                                  convertUTCToLocal(
                                    campaign.start_date,
                                    workspaceTimezone,
                                  ).localDate
                                }
                                <ArrowRightIcon />
                                {
                                  convertUTCToLocal(
                                    campaign.end_date,
                                    workspaceTimezone,
                                  ).localDate
                                }
                              </div>
                            </TableCell>
                            <TableCell className='py-4 px-3 text-center align-middle'>
                              {campaign.traveller_count_total}
                            </TableCell>

                            {/* Sent Cell */}
                            <TableCell className='py-4 px-3 text-left align-middle'>
                              {campaign.sent}
                            </TableCell>
                            <TableCell className='py-4 px-3 text-left align-middle'>
                              {campaign.total_messages_sent}
                            </TableCell>
                            {/* Menu */}
                            <TableCell className='text-right pr-4 py-4 align-middle'>
                              {(() => {
                                const status = (
                                  campaign.status || ''
                                ).toLowerCase();
                                const isPaused = status.includes('paused');
                                const isCompleted =
                                  status.includes('completed');
                                const isRejected = status.includes('rejected'); // optional terminal state
                                const isSandbox = status.includes('sandbox');

                                const closeMenu = () => setOpenMenuRowId(null);

                                return (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        type='button'
                                        className='inline-flex h-8 w-8 items-center justify-center rounded hover:bg-muted'
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOpenMenuRowId(
                                            openMenuRowId ===
                                              campaign.campaign_id
                                              ? null
                                              : campaign.campaign_id,
                                          );
                                        }}
                                      >
                                        <DotsHorizontalIcon className='w-5 h-5' />
                                      </button>
                                    </DropdownMenuTrigger>

                                    {openMenuRowId === campaign.campaign_id && (
                                      <DropdownMenuContent
                                        align='end'
                                        className='w-40 bg-white shadow-lg rounded p-1 text-sm z-[9999]'
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {/* Pause / Resume — hidden for sandbox campaigns */}
                                        {!isSandbox &&
                                          (isPaused ? (
                                            <DropdownMenuItem
                                              onClick={async () => {
                                                closeMenu();
                                                await toggleCampaignStatus(
                                                  campaign.campaign_id,
                                                  'active',
                                                );
                                              }}
                                              className='px-3 py-2 hover:bg-gray-100 cursor-pointer'
                                            >
                                              Resume
                                            </DropdownMenuItem>
                                          ) : (
                                            <DropdownMenuItem
                                              disabled={
                                                isCompleted || isRejected
                                              }
                                              onClick={async () => {
                                                if (isCompleted || isRejected)
                                                  return;
                                                closeMenu();
                                                await toggleCampaignStatus(
                                                  campaign.campaign_id,
                                                  'paused',
                                                );
                                              }}
                                              className={`px-3 py-2 ${
                                                isCompleted || isRejected
                                                  ? 'opacity-50 cursor-not-allowed'
                                                  : 'hover:bg-gray-100 cursor-pointer'
                                              }`}
                                            >
                                              Pause
                                            </DropdownMenuItem>
                                          ))}

                                        <DropdownMenuItem
                                          onClick={() => {
                                            closeMenu();
                                            setDetailedSummaryModal({
                                              isOpen: true,
                                              campaign: campaign,
                                            });
                                          }}
                                          className='px-3 py-2 hover:bg-gray-100 cursor-pointer'
                                        >
                                          View Detailed Summary
                                        </DropdownMenuItem>
                                        {[
                                          'live',
                                          'partially live',
                                          'live with warning',
                                        ].includes(campaign.status_preview) && (
                                          <DropdownMenuItem
                                            onClick={() => {
                                              setQuickSendModal({
                                                isOpen: true,
                                                campaign: campaign,
                                              });
                                            }}
                                            className='px-3 py-2 hover:bg-gray-100 cursor-pointer'
                                          >
                                            Quick Send
                                          </DropdownMenuItem>
                                        )}
                                      </DropdownMenuContent>
                                    )}
                                  </DropdownMenu>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          className='text-center py-4 text-gray-500 font-medium'
                        >
                          {isLoading || isClearingSearch
                            ? 'Loading...'
                            : searchTerm.trim() !== ''
                              ? 'No campaigns found for the searched item.'
                              : dateRange?.from && dateRange?.to
                                ? 'No campaigns found within selected date range.'
                                : filterData.filter !== 'None' &&
                                    filterData.filter !== ''
                                  ? 'No campaigns found for the filtered item.'
                                  : 'Loading...'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
            {/* --- Dialog --- */}
            <Dialog
              open={isAlertOpen}
              onClose={handleClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
              sx={{ '& .MuiPaper-root': { borderRadius: '10px' } }}
            >
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to delete this campaign ?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  variant='outline'
                  className='w-24'
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button className='w-24' onClick={confirmDelete} autoFocus>
                  OK
                </Button>
              </DialogActions>
            </Dialog>

            {/* --- Detailed Summary --- */}
            <Dialog
              open={detailedSummaryModal.isOpen}
              onClose={() =>
                setDetailedSummaryModal({ isOpen: false, campaign: null })
              }
              aria-labelledby='detailed-summary-title'
              maxWidth='xs'
              fullWidth={false}
              PaperProps={{
                sx: {
                  borderRadius: '10px',
                  p: 1,
                  width: 420, // ⬅️ narrower dialog
                  maxWidth: 'calc(100% - 32px)',
                },
              }}
            >
              <DialogTitle id='detailed-summary-title' sx={{ pb: '12px' }}>
                <div className='text-left text-base font-semibold text-gray-900'>
                  Campaign Name :{' '}
                  <span className='text-[#3A85F7]'>
                    {detailedSummaryModal.campaign?.campaign_name}
                  </span>
                </div>
              </DialogTitle>

              <DialogContent sx={{ pt: 0 }}>
                <div className='space-y-5'>
                  {/* Campaign Section */}
                  <div>
                    <h3 className='text-sm font-semibold text-gray-800 mb-2'>
                      Campaign
                    </h3>

                    <div className='space-y-2'>
                      {detailedSummaryModal.campaign?.channels?.includes(
                        'WhatsApp',
                      ) && (
                        <div
                          className='flex justify-between items-center p-3 rounded-lg'
                          style={{
                            backgroundColor: 'rgba(71, 158, 152, 0.10)',
                            border: '1px solid #479E98',
                          }}
                        >
                          <div className='flex items-center gap-2'>
                            <div
                              className='w-3 h-3 rounded-full'
                              style={{ backgroundColor: '#479E98' }}
                            />
                            <span className='text-sm font-medium'>
                              WhatsApp Delivered
                            </span>
                          </div>

                          <span
                            className='font-semibold'
                            style={{ color: '#479E98' }}
                          >
                            {detailedSummaryModal.campaign
                              ?.delivered_whatsapp_count || 0}
                          </span>
                        </div>
                      )}

                      {detailedSummaryModal.campaign?.channels?.includes(
                        'SMS',
                      ) && (
                        <div
                          className='flex justify-between items-center p-3 rounded-lg'
                          style={{
                            backgroundColor: 'rgba(223, 165, 72, 0.12)',
                            border: '1px solid #DFA548',
                          }}
                        >
                          <div className='flex items-center gap-2'>
                            <div
                              className='w-3 h-3 rounded-full'
                              style={{ backgroundColor: '#DFA548' }}
                            />
                            <span className='text-sm font-medium'>
                              SMS Delivered
                            </span>
                          </div>

                          <span
                            className='font-semibold'
                            style={{ color: '#DFA548' }}
                          >
                            {detailedSummaryModal.campaign
                              ?.delivered_sms_count || 0}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quick Send Section */}
                  <div>
                    <h3 className='text-sm font-semibold text-gray-800 mb-2'>
                      Quick Send
                    </h3>

                    <div className='space-y-2'>
                      {/* Quick Send Sent */}
                      <div
                        className='flex justify-between items-center p-3 rounded-lg'
                        style={{
                          backgroundColor: 'rgba(59, 130, 246, 0.10)',
                          border: '1px solid #3B82F6',
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: '#3B82F6' }}
                          />
                          <span className='text-sm font-medium text-gray-700'>
                            Sent Messages
                          </span>
                        </div>

                        <span
                          className='font-semibold'
                          style={{ color: '#3B82F6' }}
                        >
                          {detailedSummaryModal.campaign?.sent_quick_send_count}
                        </span>
                      </div>

                      {/* Quick Send Delivered */}
                      <div
                        className='flex justify-between items-center p-3 rounded-lg'
                        style={{
                          backgroundColor: 'rgba(139, 92, 246, 0.10)',
                          border: '1px solid #8B5CF6',
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: '#8B5CF6' }}
                          />
                          <span className='text-sm font-medium text-gray-700'>
                            Delivered Messages
                          </span>
                        </div>

                        <span
                          className='font-semibold'
                          style={{ color: '#8B5CF6' }}
                        >
                          {
                            detailedSummaryModal.campaign
                              ?.delivered_quick_send_count
                          }
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Test Mode Section */}
                  <div>
                    <h3 className='text-sm font-semibold text-gray-800 mb-2'>
                      Test Mode
                    </h3>

                    <div className='space-y-2'>
                      {/* Test Mode Sent */}
                      <div
                        className='flex justify-between items-center p-3 rounded-lg'
                        style={{
                          backgroundColor: 'rgba(249, 115, 22, 0.10)',
                          border: '1px solid #F97316',
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: '#F97316' }}
                          />
                          <span className='text-sm font-medium text-gray-700'>
                            Sent Messages
                          </span>
                        </div>

                        <span
                          className='font-semibold'
                          style={{ color: '#F97316' }}
                        >
                          {detailedSummaryModal.campaign?.sent_test_mode_count}
                        </span>
                      </div>

                      {/* Test Mode Delivered */}
                      <div
                        className='flex justify-between items-center p-3 rounded-lg'
                        style={{
                          backgroundColor: 'rgba(236, 72, 153, 0.10)',
                          border: '1px solid #EC4899',
                        }}
                      >
                        <div className='flex items-center gap-2'>
                          <div
                            className='w-3 h-3 rounded-full'
                            style={{ backgroundColor: '#EC4899' }}
                          />
                          <span className='text-sm font-medium text-gray-700'>
                            Delivered Messages
                          </span>
                        </div>

                        <span
                          className='font-semibold'
                          style={{ color: '#EC4899' }}
                        >
                          {
                            detailedSummaryModal.campaign
                              ?.delivered_test_mode_count
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogContent>

              <DialogActions sx={{ pt: '8px', justifyContent: 'center' }}>
                <Button
                  variant='outline'
                  onClick={() =>
                    setDetailedSummaryModal({ isOpen: false, campaign: null })
                  }
                  className='w-20 cursor-pointer'
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* QUICK SEND BUTTON */}
            <Dialog
              open={quickSendModal.isOpen}
              onClose={() =>
                setQuickSendModal({ isOpen: false, campaign: null })
              }
              fullWidth
              maxWidth={false}
              TransitionProps={{ timeout: 0 }}
              PaperProps={{
                sx: {
                  width: '1100px',
                  maxWidth: '90%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: '90vh', // ← constrains height so footer stays visible
                  zIndex: 1300,
                },
              }}
              sx={{
                zIndex: 1300,
                '& .MuiBackdrop-root': { zIndex: 1300 },
              }}
            >
              {/* Scrollable content */}
              <DialogContent
                sx={{
                  p: 2,
                  overflowY: 'auto', // ← enables scroll inside content area
                  flex: '1 1 auto', // ← content grows but doesn't push footer out
                }}
              >
                <CampaignSelectionSection
                  campaign={quickSendModal.campaign}
                  onSend={handleQuickSend}
                  sendTrigger={sendTrigger}
                />
              </DialogContent>

              {/* Footer — pinned at bottom, always visible */}
              <DialogActions
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: '1px solid #e5e7eb',
                  flexShrink: 0,
                }}
              >
                <Button
                  variant='outline'
                  className='px-6 py-3 text-sm w-[100px]'
                  onClick={() =>
                    setQuickSendModal({ isOpen: false, campaign: null })
                  }
                >
                  Close
                </Button>
                <Button
                  variant='default'
                  className='w-[100px] bg-[#3A85F7] text-white hover:bg-blue-600'
                  onClick={() => setSendTrigger((prev) => prev + 1)}
                >
                  Send
                </Button>
              </DialogActions>
            </Dialog>

            {/* QuickSend Delivery Result Dialog */}
            <Dialog
              open={deliveryResult.isOpen}
              onClose={() => setDeliveryResult({ isOpen: false, data: null })}
              aria-labelledby='quicksend-delivery-title'
              maxWidth='xs'
              fullWidth={false}
              PaperProps={{
                sx: {
                  borderRadius: '10px',
                  p: 1,
                  width: 420,
                  maxWidth: 'calc(100% - 32px)',
                },
              }}
            >
              <DialogTitle id='quicksend-delivery-title' sx={{ pb: '12px' }}>
                <div className='text-left text-base font-semibold text-gray-900'>
                  QuickSend Delivery
                </div>
              </DialogTitle>

              <DialogContent sx={{ pt: 0 }}>
                {deliveryResult.data && (
                  <div className='space-y-4'>
                    {/* Total Count Row */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(58, 133, 247, 0.10)',
                        border: `1px solid #3A85F7`,
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#3A85F7' }}
                        />
                        <span className='text-gray-700 font-medium text-sm'>
                          Total Count
                        </span>
                      </div>
                      <span
                        className='font-semibold text-sm'
                        style={{ color: '#3A85F7' }}
                      >
                        {deliveryResult.data.total}
                      </span>
                    </div>

                    {/* Total Sent Row */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(71, 158, 152, 0.10)',
                        border: `1px solid #479E98`,
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#479E98' }}
                        />
                        <span className='text-gray-700 font-medium text-sm'>
                          Total Sent
                        </span>
                      </div>
                      <span
                        className='font-semibold text-sm'
                        style={{ color: '#479E98' }}
                      >
                        {deliveryResult.data.success_count}
                      </span>
                    </div>

                    {/* Failed Row */}
                    <div
                      className='flex justify-between items-center p-3 rounded-lg'
                      style={{
                        backgroundColor: 'rgba(223, 100, 72, 0.10)',
                        border: `1px solid #DF6448`,
                      }}
                    >
                      <div className='flex items-center gap-2'>
                        <div
                          className='w-3 h-3 rounded-full'
                          style={{ backgroundColor: '#DF6448' }}
                        />
                        <span className='text-gray-700 font-medium text-sm'>
                          Failed
                        </span>
                      </div>
                      <span
                        className='font-semibold text-sm'
                        style={{ color: '#DF6448' }}
                      >
                        {deliveryResult.data.failure_count}
                      </span>
                    </div>
                  </div>
                )}
              </DialogContent>

              <DialogActions sx={{ pt: '8px', justifyContent: 'center' }}>
                <Button
                  variant='outline'
                  onClick={() =>
                    setDeliveryResult({ isOpen: false, data: null })
                  }
                  className='w-20 cursor-pointer'
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {/* --- Pagination controls --- */}
            <div className='flex justify-between items-center mt-4'>
              <div className='flex items-center space-x-2 text-gray-500 text-sm '>
                <span>{`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
                  currentPage * rowsPerPage,
                  totalCount,
                )} of ${totalCount} row(s) selected`}</span>
              </div>
              <div className='flex items-center space-x-4 font-medium text-sm'>
                <span className='text-[#020617] font-medium text-[14px]'>
                  Rows per page
                </span>
                <div className='relative inline-block ml-2'>
                  <select
                    disabled={currentCampaigns.length === 0}
                    className={`cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${currentCampaigns.length === 0 ? 'cursor-not-allowed bg-gray-100' : ''}`}
                    style={{
                      width: '60px',
                      height: '30px',
                      textAlign: 'left',
                      fontSize: '12px',
                      fontWeight: '400',
                      borderColor: '#E5E7EB',
                      boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                    value={currentCampaigns.length === 0 ? 0 : rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {[5, 10, 20].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                  <CaretSortIcon className='absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4' />
                </div>
                <div className='ml-4 mr-4'>
                  <span className='text-[#020617] text-[14px] font-medium'>{`Page ${currentPage} of ${totalPages}`}</span>
                </div>
                <div className='flex items-center gap-2'>
                  <button
                    disabled={
                      currentPage === 1 || currentCampaigns.length === 0
                    }
                    className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 || currentCampaigns.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                    onClick={() => handlePageChange(1)}
                  >
                    «
                  </button>
                  <button
                    disabled={
                      currentPage === 1 || currentCampaigns.length === 0
                    }
                    className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === 1 || currentCampaigns.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    ‹
                  </button>
                  <button
                    disabled={
                      currentPage === totalPages ||
                      currentCampaigns.length === 0
                    }
                    className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages || currentCampaigns.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    ›
                  </button>
                  <button
                    disabled={
                      currentPage === totalPages ||
                      currentCampaigns.length === 0
                    }
                    className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${currentPage === totalPages || currentCampaigns.length === 0 ? 'cursor-not-allowed bg-gray-100' : 'hover:bg-gray-200'}`}
                    onClick={() => handlePageChange(totalPages)}
                  >
                    »
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          {isLoading && null}
          {!isLoading && (
            <div className='flex flex-col items-center justify-center h-[500px]'>
              <h2 className='text-[24px] font-semibold mb-1 text-[#000000]'>
                Here you will see all campaigns
              </h2>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminCampaignList;
